/**
 * ResultsEditPreviewDialog Component
 *
 * Preview dialog for pending changes in query editor results edit mode.
 * Groups changes by source table, generates SQL preview via IPC,
 * and allows user to execute or open in a new query editor tab.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Code2, Copy, Info, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import type { PendingUpdate, TransactionMode, TransactionState } from '../../core/types/workspaceTypes';
import type { EditableColumnInfo, EditableTableInfo } from '../utils/editabilityDetection';
import { previewTableExplorerChanges } from '../../table-explorer/services/tableExplorerApi';
import { writeClipboardText } from '@/lib/clipboard';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type ResultsEditPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingChanges: PendingUpdate[];
  editableColumns: Map<string, EditableColumnInfo>;
  editableTables: Map<string, EditableTableInfo>;
  connectionId: string;
  database: string;
  onExecute: () => void;
  onOpenInQueryEditor?: (query: string) => void;
  transactionMode?: TransactionMode;
  transactionState?: TransactionState;
};

export function ResultsEditPreviewDialog({
  open,
  onOpenChange,
  pendingChanges,
  editableColumns,
  editableTables,
  connectionId,
  database,
  onExecute,
  onOpenInQueryEditor,
  transactionMode = 'auto',
  transactionState = 'idle',
}: ResultsEditPreviewDialogProps) {
  const [previewSql, setPreviewSql] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate preview SQL when dialog opens
  useEffect(() => {
    if (!open || pendingChanges.length === 0) return;

    let cancelled = false;
    setIsLoading(true);
    setPreviewSql('');
    setError(null);

    const generatePreview = async () => {
      // Group changes by source table (same logic as resultsEditingApi)
      const changesByTable = new Map<string, PendingUpdate[]>();
      for (const change of pendingChanges) {
        const colInfo = editableColumns.get(change.field);
        if (!colInfo) continue;
        const tableKey = `${colInfo.schema}.${colInfo.table}`;
        const existing = changesByTable.get(tableKey) || [];
        existing.push({
          ...change,
          field: colInfo.sourceColumn,
        });
        changesByTable.set(tableKey, existing);
      }

      const sqlParts: string[] = [];

      for (const [tableKey, changes] of changesByTable) {
        const tableInfo = editableTables.get(tableKey);
        if (!tableInfo) continue;

        try {
          const result = await previewTableExplorerChanges({
            connectionId,
            table: { database, schema: tableInfo.schema, tableName: tableInfo.table },
            changes,
          });
          if (result.success && result.sql) {
            sqlParts.push(`-- ${tableKey}\n${result.sql}`);
          } else {
            sqlParts.push(`-- ${tableKey}: ${result.error || 'Failed to generate preview'}`);
          }
        } catch (err) {
          sqlParts.push(`-- ${tableKey}: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }

      if (!cancelled) {
        setPreviewSql(sqlParts.join('\n\n'));
        setIsLoading(false);
      }
    };

    void generatePreview();
    return () => { cancelled = true; };
  }, [open, pendingChanges, editableColumns, editableTables, connectionId, database]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setIsExecuting(false);
    }
  }, [open]);

  const handleExecute = useCallback(async () => {
    setIsExecuting(true);
    setError(null);
    try {
      onExecute();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsExecuting(false);
    }
  }, [onExecute, onOpenChange]);

  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await writeClipboardText(previewSql);
    if (!ok) {
      const textArea = document.createElement('textarea');
      textArea.value = previewSql;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [previewSql]);

  const updateCount = pendingChanges.length;
  const hasActiveTransaction = transactionState === 'active';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="results-edit-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5" />
            Preview Changes
          </DialogTitle>
          <DialogDescription>
            {updateCount} update{updateCount !== 1 ? 's' : ''} will be applied:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* SQL Preview */}
          <div className="relative">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <RandomDotmSpinner size={20} className="mr-2" />
                Generating SQL preview...
              </div>
            ) : (
              <>
                <pre
                  className="bg-muted/50 border border-border rounded-md p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto"
                  data-testid="results-edit-preview-sql"
                >
                  {previewSql || '-- No changes to preview'}
                </pre>

                {previewSql && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="absolute top-2 right-2 h-7 px-2"
                    data-testid="results-edit-preview-copy-button"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1 text-green-500" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Warning / Info */}
          {hasActiveTransaction ? (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
              <span>Changes will be executed within the active transaction. You can rollback if needed.</span>
            </div>
          ) : transactionMode === 'auto' ? (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <span>Auto-commit mode: changes will be applied immediately and cannot be undone.</span>
            </div>
          ) : transactionState === 'error' ? (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <span>Transaction error state: changes will be applied immediately and cannot be rolled back.</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
              <span>{transactionMode === 'manual' ? 'Manual' : 'Smart'} mode: a transaction begins before these changes run. You can commit or roll back afterwards.</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExecuting}>
            Cancel
          </Button>

          {onOpenInQueryEditor && previewSql && !isLoading && (
            <Button
              variant="outline"
              onClick={() => {
                onOpenInQueryEditor(previewSql);
                onOpenChange(false);
              }}
              disabled={isExecuting}
              data-testid="results-edit-preview-open-in-editor"
            >
              Open in new tab
            </Button>
          )}

          <Button
            onClick={handleExecute}
            disabled={isExecuting || isLoading || pendingChanges.length === 0}
            data-testid="results-edit-preview-execute"
          >
            {isExecuting ? (
              <>
                <RandomDotmSpinner size={16} className="mr-2" />
                Executing...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Execute Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
